import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/apiClient";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      nav("/dashboard", { replace: true });
    } catch (e2) {
      const msg = formatApiError(e2?.response?.data?.detail) || e2.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-surface" data-testid="login-page">
      {/* Left: hero with equipment showcase */}
      <div className="relative hidden lg:block bg-[#0F2A1B] overflow-hidden">
        {/* Faint backdrop */}
        <div className="absolute inset-0 opacity-[0.18]" style={{ background: "radial-gradient(circle at 30% 20%, #C9A55B 0%, transparent 50%), radial-gradient(circle at 80% 75%, #2D6A3F 0%, transparent 60%)" }} />
        {/* Two equipment images, top + middle, behind text */}
        <img src="/equipment/harrow.png" alt="" className="absolute right-[-6%] top-[6%] w-[72%] object-contain drop-shadow-[0_30px_70px_rgba(0,0,0,0.55)] opacity-[0.92]" data-testid="login-hero-harrow" />
        <img src="/equipment/tiller.png" alt="" className="absolute -left-[10%] bottom-[-4%] w-[78%] object-contain drop-shadow-[0_30px_70px_rgba(0,0,0,0.55)] opacity-[0.78]" data-testid="login-hero-tiller" />
        {/* Gradient veil for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(15,42,27,0.78)] via-[rgba(15,42,27,0.30)] to-[rgba(15,42,27,0.92)]" />

        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center overflow-hidden p-1">
              <img src="https://customer-assets.emergentagent.com/job_eway-commerce/artifacts/jzpce29v_hans%20logo.jpeg" alt="logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight">Ramkishan Agri Innovate</div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/70">हंसा · Billing & GST Suite</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#E8D9A8] mb-4">Built for Indian Manufacturers</div>
            <h2 className="font-display text-4xl font-bold leading-tight mb-4 drop-shadow-md">Disc Harrows. Tillers. Tax Invoices. All from one ledger.</h2>
            <p className="text-white/85 text-sm leading-relaxed drop-shadow-sm">Quotations, Sales Orders, Tax Invoices, Purchase Orders, Delivery Challans and Gate Passes — Tally-format prints, GSTR-1 / GSTR-3B / Tally XML exports, FY-aware numbering, and WhatsApp share built-in.</p>
          </div>
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-white/55">
            <span>GSTIN 09AAOCR7303L1ZU</span>
            <span>Made for Meerut's Agri Trade</span>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12 animate-fade-up">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-12 h-12 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden p-1">
              <img src="https://customer-assets.emergentagent.com/job_eway-commerce/artifacts/jzpce29v_hans%20logo.jpeg" alt="logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight text-ink">Ramkishan Agri</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">हंसा · Billing Suite</div>
            </div>
          </div>

          <div className="label-overline mb-3">Sign in to continue</div>
          <h1 className="font-display text-3xl font-bold text-ink mb-2 tracking-tight">Welcome back</h1>
          <p className="text-sm text-ink-muted mb-8">Enter your credentials to access the billing suite.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-overline block mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-input rounded-md px-3 py-2.5 text-sm text-ink focus:ring-2 focus:ring-brand-secondary focus:border-brand transition-all outline-none"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label-overline block mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-input rounded-md px-3 py-2.5 pr-10 text-sm text-ink focus:ring-2 focus:ring-brand-secondary focus:border-brand transition-all outline-none"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-brand p-1" data-testid="toggle-password-visibility">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err && <div className="text-sm text-destructive bg-[#FCEAEA] border border-[#F5C6C6] rounded-md px-3 py-2" data-testid="login-error">{err}</div>}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full btn-primary py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
